import { fetchData, postData, patchData, deleteData } from 'modules/Axios';
import { showToast } from 'modules/Notifications';
import { sortByPropAscendingCaseInsensitive } from 'lib/Sorting';

export const defaultState = {
  userData: {},
  userBrands: [],
  simpleSsoIntegrations: [],
  snowflakeConfig: [],
  hexConfig: [],
  thoughtSpotConfig: [],
  dataLoading: true,
  pageLoading: true,
};

export const RESET_USERS_ROUTE_DATA = 'RESET_USERS_ROUTE_DATA';
export const resetRouteData = () => ({
  type: RESET_USERS_ROUTE_DATA,
});

export const USER_DATA_LOADING = 'USER_DATA_LOADING';
export const dataLoading = (payload) => ({
  type: USER_DATA_LOADING,
  payload,
});

export const USER_PAGE_LOADING = 'USER_PAGE_LOADING';
export const pageLoading = (payload) => ({
  type: USER_PAGE_LOADING,
  payload,
});

export const getUsers = () => {
  return async (dispatch) => {
    try {
      return await dispatch(fetchData('/users?size=100000'));
    } catch (e) {
      console.error('API Call to /users failed...', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue retrieving users list. Please try again later.',
          dismissible: true,
          dismissAfter: 5000,
        })
      );
      return null;
    }
  };
};

export const getAppUsers = () => {
  return async (dispatch) => {
    try {
      return await dispatch(fetchData('/users?type=APPLICATION&size=100000'));
    } catch (e) {
      console.error('API Call to /users failed...', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue retrieving application users list. Please try again later.',
          dismissible: true,
          dismissAfter: 5000,
        })
      );
      return null;
    }
  };
};

export const initUser = (userId) => {
  return async (dispatch) => {
    try {
      const userData = await dispatch(getUserById(userId));
      dispatch(setUser(userData));
      await Promise.all([
        dispatch(getUserBrands(userId)),
        dispatch(getUserSimpleSsoIntegrations(userId)),
        dispatch(getIntegrationStatuses(userId)),
      ]);
      dispatch(dataLoading(false));
      dispatch(pageLoading(false));
    } catch (e) {
      console.error('API Call to /users failed...', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue retrieving the user. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
    }
  };
};

export const getUserById = (userId) => {
  return async (dispatch) => {
    try {
      const userData = await dispatch(fetchData(`/users/${userId}`));
      dispatch(setUser(userData));
      return userData;
    } catch (e) {
      console.error('API Call to /users failed...', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue retrieving the user. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
      return defaultState.userData;
    }
  };
};

export const createUser = (userBody, isApplicationUser) => {
  const url = isApplicationUser ? '/users/application' : '/users';
  return async (dispatch) => {
    try {
      const user = await dispatch(postData(url, userBody));
      dispatch(
        showToast({
          state: 'SUCCESS',
          title: 'Success',
          message: 'User’s account has been created',
          dismissible: true,
          dismissAfter: 2000,
        })
      );
      return user;
    } catch (e) {
      console.error(`API Call to ${url} failed...`, e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue creating the user\'s account. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
      return null;
    }
  };
};

export const updateUser = (userId, userBody, isApplicationUser) => {
  const url = isApplicationUser ? `/users/application/${userId}` : `/users/${userId}`;
  return async (dispatch) => {
    try {
      const user = await dispatch(patchData(url, userBody));
      dispatch(
        showToast({
          state: 'SUCCESS',
          title: 'Success',
          message: 'User’s account has been updated',
          dismissible: true,
          dismissAfter: 2000,
        })
      );
      return user;
    } catch (e) {
      console.error(`API Call to ${url} failed...`, e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue updating the user\'s account. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
      return null;
    }
  };
};

export const SET_USER_DATA = 'SET_USER_DATA';
export const setUser = (payload) => ({
  type: SET_USER_DATA,
  payload,
});

export const userActivation = (userId, isUserActive, isTable) => {
  return async (dispatch) => {
    const URL = `/users/${userId}/${isUserActive ? 'disable' : 'enable'}`;
    try {
      if (isTable) {
        const userData = await dispatch(postData(URL));
        dispatch(setUser(userData));
      } else {
        dispatch(dataLoading(true));
        const userData = await dispatch(postData(URL));
        await dispatch(getIntegrationStatuses(userId));
        dispatch(setUser(userData));
      }
      dispatch(
        showToast({
          state: 'SUCCESS',
          title: 'Success',
          message: `User’s account has been  ${
            isUserActive ? 'disabled' : 'enabled'
          }`,
          dismissible: true,
          dismissAfter: 2000,
        })
      );
    } catch (e) {
      console.error('API Call to /users failed...', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message:
            'Issue when trying to enable/disable the user. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
    } finally {
      dispatch(dataLoading(false));
    }
  };
};

export const updateUserRole = (userId, reguestBody) => {
  return async (dispatch) => {
    const URL = `/users/${userId}/roles`;
    try {
      dispatch(dataLoading(true));
      const userData = await dispatch(patchData(URL, reguestBody));
      await dispatch(getIntegrationStatuses(userId));
      dispatch(setUser(userData));
      dispatch(
        showToast({
          state: 'SUCCESS',
          title: 'Success',
          message: 'User role has been updated',
          dismissible: true,
          dismissAfter: 2000,
        })
      );
      return userData;
    } catch (e) {
      console.error('API Call to /users failed...', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue when trying to update user\'s role. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
      return null;
    } finally {
      dispatch(dataLoading(false));
    }
  };
};

// USERS BRANDS

export const SET_USER_BRANDS = 'SET_USER_BRANDS';
export const setUserBrands = (payload) => ({
  type: SET_USER_BRANDS,
  payload,
});

export const getUserBrands = (userId) => {
  return async (dispatch) => {
    try {
      const userBrands = await dispatch(fetchData(`/users/${userId}/brands`));
      dispatch(setUserBrands(userBrands));
    } catch (e) {
      console.error('API Call to /users failed...', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue retrieving the user\'s brands. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
    }
  };
};

// USERS INTEGRATIONS

export const SET_USER_SIMPLE_SSO_INTEGRATIONS = 'SET_USER_SIMPLE_SSO_INTEGRATIONS';
export const setUserSimpleSsoIntegrations = (payload) => ({
  type: SET_USER_SIMPLE_SSO_INTEGRATIONS,
  payload,
});

export const getUserSimpleSsoIntegrations = (userId) => {
  return async (dispatch) => {
    try {
      const simpleSsoIntegrations = await dispatch(fetchData(`/users/${userId}/integrations`));
      dispatch(setUserSimpleSsoIntegrations(simpleSsoIntegrations));
    } catch (e) {
      console.error('API Call to /users failed...', e);
    }
  };
};

export const getIntegrationStatuses = (userId) => {
  return async (dispatch) => {
    try {
      await Promise.all([
        dispatch(getUserSnowflakeConfig(userId)),
        dispatch(getUserHexConfig(userId)),
        dispatch(getUserThoughtSpotConfig(userId)),
      ]);
    } catch (e) {
      console.error('Error getting integration configurations:', e);
    }
  };
};

export const simpleSsoIntegrationActivation = (userId, applicationId, isActive) => {
  return async (dispatch) => {
    const URL = `/users/${userId}/integrations/${applicationId}/${isActive ? 'disable' : 'enable'}`;
    try {
      await dispatch(postData(URL));
      await dispatch(getUserSimpleSsoIntegrations(userId));
      dispatch(
        showToast({
          state: 'SUCCESS',
          title: 'Success',
          message: `Integration has been ${isActive ? 'disabled' : 'enabled'}`,
          dismissible: true,
          dismissAfter: 2000,
        })
      );
    } catch (e) {
      console.error('API Call to /users failed...', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message:
            'Issue when trying to enable/disable the integration. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
    }
  };
};

export const SET_USER_SNOWFLAKE_CONFIG = 'SET_USER_SNOWFLAKE_CONFIG';
export const SET_USER_HEX_CONFIG = 'SET_USER_HEX_CONFIG';
export const SET_USER_THOUGHTSPOT_CONFIG = 'SET_USER_THOUGHTSPOT_CONFIG';
const getSetUserIntegrationConfigActionType = (integrationType) => {
  switch (integrationType) {
    case 'snowflake':
      return SET_USER_SNOWFLAKE_CONFIG;
    case 'hex':
      return SET_USER_HEX_CONFIG;
    case 'thoughtspot':
      return SET_USER_THOUGHTSPOT_CONFIG;
    default:
      console.error('Invalid integration type:', integrationType);
      throw new Error(`Invalid integration type: ${integrationType}`);
  }
};

const setUserIntegrationConfig = (payload, integrationType) => {
  const type = getSetUserIntegrationConfigActionType(integrationType);
  return { type, payload };
};

const getUserIntegrationConfig = (userId, integrationType) => {
  return async (dispatch) => {
    try {
      const config = await dispatch(fetchData(`/users/${userId}/integrations/${integrationType}/config`));
      const sortedConfig = sortByPropAscendingCaseInsensitive(
        config?.map((integration) => ({ ...integration, name: integration?.connection?.name })), 'name'
      );
      dispatch(setUserIntegrationConfig(sortedConfig, integrationType));
    } catch (e) {
      console.error('Error getting user integration config:', e);
    }
  };
};

export const getIntegrationDefaultGroups = (userId, connectionId, integrationType) => {
  return async (dispatch) => {
    try {
      const defaultGroups = await dispatch(
        fetchData(`/users/${userId}/integrations/${integrationType}/config/default-permissions?connectionId=${connectionId}`)
      );
      return defaultGroups;
    } catch (e) {
      console.error('Error getting integration default groups/roles:', e);
      dispatch(showToast({
        state: 'ERROR',
        title: 'Error',
        message: 'Issue retrieving the default roles or groups for this connection. Please try again later',
        dismissible: true,
        dismissAfter: 1500,
      }));
      return null;
    }
  };
};

export const externalIntegrationActivation = (userId, externalUserId, isActive, integrationType) => {
  return async (dispatch) => {
    const URL = `/users/${userId}/integrations/${integrationType}/config/${externalUserId}/${isActive ? 'disable' : 'enable'}`;
    try {
      await dispatch(postData(URL));
      await dispatch(getUserIntegrationConfig(userId, integrationType));
      dispatch(
        showToast({
          state: 'SUCCESS',
          title: 'Success',
          message: `Integration has been ${isActive ? 'disabled' : 'enabled'}`,
          dismissible: true,
          dismissAfter: 2000,
        })
      );
    } catch (e) {
      console.error('Error enabling or disabling integration:', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message:
            'Issue when trying to enable/disable the integration. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
    }
  };
};

export const createExternalIntegration = (userId, requestBody, integrationType) => {
  return async (dispatch) => {
    const URL = `/users/${userId}/integrations/${integrationType}/config`;
    try {
      await dispatch(postData(URL, requestBody));
      await dispatch(getUserIntegrationConfig(userId, integrationType));
      dispatch(
        showToast({
          state: 'SUCCESS',
          title: 'Success',
          message: 'New integration added',
          dismissible: true,
          dismissAfter: 2000,
        })
      );
    } catch (e) {
      console.error('Error creating new integration:', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message:
            'Issue when trying to add new integration. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
    }
  };
};

export const updateExternalIntegration = (userId, externalUserId, requestBody, integrationType) => {
  return async (dispatch) => {
    const URL = `/users/${userId}/integrations/${integrationType}/config/${externalUserId}`;
    try {
      await dispatch(patchData(URL, requestBody));
      await dispatch(getUserIntegrationConfig(userId, integrationType));
      dispatch(
        showToast({
          state: 'SUCCESS',
          title: 'Success',
          message: 'Integration Settings have been saved',
          dismissible: true,
          dismissAfter: 2000,
        })
      );
    } catch (e) {
      console.error('Error updating an integration:', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message:
            'Issue when trying to update an integration. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
    }
  };
};

export const removeExternalIntegration = (userId, externalUserId, integrationType) => {
  return async (dispatch) => {
    const URL = `/users/${userId}/integrations/${integrationType}/config/${externalUserId}`;
    try {
      await dispatch(deleteData(URL));
      await dispatch(getUserIntegrationConfig(userId, integrationType));
      dispatch(
        showToast({
          state: 'SUCCESS',
          title: 'Success',
          message: 'Integration has been removed',
          dismissible: true,
          dismissAfter: 2000,
        })
      );
    } catch (e) {
      console.error('Error removing an integration:', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message:
            'Issue when trying to delete the integration. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
    }
  };
};

export const hideExternalUnreadAlert = (userId, externalUserId, unreadAlertId, integrationType) => {
  return async (dispatch) => {
    try {
      await dispatch(patchData(`/users/${userId}/integrations/${integrationType}/config/${externalUserId}/permissions/audit-log/${unreadAlertId}`));
      await dispatch(getUserIntegrationConfig(userId, integrationType));
    } catch (e) {
      console.error(`API Call to /users/{userId}/integrations/${integrationType}/config/{externalUserId}/permissions/audit-log/{id} failed...`, e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message: `Issue hidding the ${integrationType} alert banner. Please try again later`,
          dismissible: true,
          dismissAfter: 1500,
        })
      );
    }
  };
};

// Snowflake Integration

const SNOWFLAKE = 'snowflake';

export const getUserSnowflakeConfig = (userId) => {
  return getUserIntegrationConfig(userId, SNOWFLAKE);
};

export const getSnowflakeDefaultRoles = (userId, connectionId) => {
  return getIntegrationDefaultGroups(userId, connectionId, SNOWFLAKE);
};

export const snowflakeIntegrationActivation = (userId, snowflakeUserId, isActive) => {
  return externalIntegrationActivation(userId, snowflakeUserId, isActive, SNOWFLAKE);
};

export const createSnowflakeIntegration = (userId, requestBody) => {
  return createExternalIntegration(userId, requestBody, SNOWFLAKE);
};

export const updateSnowflakeIntegration = (userId, snowflakeUserId, reguestBody) => {
  return updateExternalIntegration(userId, snowflakeUserId, reguestBody, SNOWFLAKE);
};

export const removeSnowflakeIntegration = (userId, snowflakeUserId) => {
  return removeExternalIntegration(userId, snowflakeUserId, SNOWFLAKE);
};

export const getSnowflakeIntegrationRoleWarehouses = (userId, snowflakeIntegrationId, roleName) => {
  return async (dispatch) => {
    const URL = `/users/${userId}/integrations/snowflake/config/${snowflakeIntegrationId}/roles/${roleName}/warehouses`;
    try {
      const warehouses = await dispatch(fetchData(URL));
      return warehouses;
    } catch (e) {
      console.error('Error fetching available Snowflake warehouses:', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message: 'Issue retrieving allowed Snowflake warehouses. Please try again later',
          dismissible: true,
          dismissAfter: 3000,
        })
      );
    }
  };
};

// Hex Integration

const HEX = 'hex';

export const getUserHexConfig = (userId) => {
  return getUserIntegrationConfig(userId, HEX);
};

export const getHexDefaultGroups = (userId, connectionId) => {
  return getIntegrationDefaultGroups(userId, connectionId, HEX);
};

export const hexIntegrationActivation = (userId, hexUserId, isActive) => {
  return externalIntegrationActivation(userId, hexUserId, isActive, HEX);
};

export const createHexIntegration = (userId, requestBody) => {
  return createExternalIntegration(userId, requestBody, HEX);
};

export const updateHexIntegration = (userId, hexUserId, requestBody) => {
  return updateExternalIntegration(userId, hexUserId, requestBody, HEX);
};

export const removeHexIntegration = (userId, hexUserId) => {
  return removeExternalIntegration(userId, hexUserId, HEX);
};

// ThoughtSpot Integration

const THOUGHTSPOT = 'thoughtspot';

export const getUserThoughtSpotConfig = (userId) => {
  return getUserIntegrationConfig(userId, THOUGHTSPOT);
};

export const getThoughtSpotDefaultGroups = (userId, connectionId) => {
  return getIntegrationDefaultGroups(userId, connectionId, THOUGHTSPOT);
};

export const thoughtSpotIntegrationActivation = (userId, thoughtSpotUserId, isActive) => {
  return externalIntegrationActivation(userId, thoughtSpotUserId, isActive, THOUGHTSPOT);
};

export const createThoughtSpotIntegration = (userId, requestBody) => {
  return createExternalIntegration(userId, requestBody, THOUGHTSPOT);
};

export const updateThoughtSpotIntegration = (userId, thoughtSpotUserId, requestBody) => {
  return updateExternalIntegration(userId, thoughtSpotUserId, requestBody, THOUGHTSPOT);
};

export const removeThoughtSpotIntegration = (userId, thoughtSpotUserId) => {
  return removeExternalIntegration(userId, thoughtSpotUserId, THOUGHTSPOT);
};

// USERS MFA

export const resetMFA = (userId) => {
  return async (dispatch) => {
    const URL = `/users/${userId}/mfa/reset`;
    try {
      await dispatch(postData(URL));
      dispatch(
        showToast({
          state: 'SUCCESS',
          title: 'Success',
          message: 'Multi-factor authentication (MFA) has been reset',
          dismissible: true,
          dismissAfter: 2000,
        })
      );
    } catch (e) {
      console.error('API Call to /users failed...', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message:
            'Issue when trying to reset a Multi-factor authentication (MFA). Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
    }
  };
};

export const activationMFA = (userId, isMFArequired) => {
  return async (dispatch) => {
    const URL = `/users/${userId}/mfa/${isMFArequired ? 'disable' : 'enable'}`;
    try {
      const userData = await dispatch(postData(URL));
      dispatch(
        showToast({
          state: 'SUCCESS',
          title: 'Success',
          message: `Multi-factor authentication (MFA) has been ${
            isMFArequired ? 'disabled' : 'enabled'
          }`,
          dismissible: true,
          dismissAfter: 2000,
        })
      );
      dispatch(setUser(userData));
    } catch (e) {
      console.error('API Call to /users failed...', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message:
            'Issue when trying to enable/disable a Multi-factor authentication (MFA). Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
    }
  };
};

// USERS EMAIL

export const resendInvitationEmail = (userId) => {
  return async (dispatch) => {
    const URL = `/users/${userId}/resend-invitation-email`;
    try {
      await dispatch(postData(URL));
      dispatch(
        showToast({
          state: 'SUCCESS',
          title: 'Success',
          message: 'The invitation has been sent',
          dismissible: true,
          dismissAfter: 2000,
        })
      );
    } catch (e) {
      console.error('API Call to /users failed...', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message:
            'Issue when trying to send an invitation to a user. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
    }
  };
};

export const sendResetPasswordEmail = (userId) => {
  return async (dispatch) => {
    const URL = `/users/${userId}/send-password-reset-email`;
    try {
      await dispatch(postData(URL));
      dispatch(
        showToast({
          state: 'SUCCESS',
          title: 'Success',
          message: 'The reset password email has been sent',
          dismissible: true,
          dismissAfter: 2000,
        })
      );
    } catch (e) {
      console.error('API Call to /users failed...', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message:
            'Issue when trying to send a reset password email to a user. Please try again later',
          dismissible: true,
          dismissAfter: 1500,
        })
      );
    }
  };
};

export const rotateAppUserSecret = (userId) => {
  return async (dispatch) => {
    const URL = `/users/application/${userId}/rotate-secret`;
    try {
      await dispatch(postData(URL));
      dispatch(
        showToast({
          state: 'SUCCESS',
          title: 'Success',
          message: 'Application user secret has been rotated',
          dismissible: true,
          dismissAfter: 3000,
        })
      );
    } catch (e) {
      console.error('API Call to /users failed...', e);
      dispatch(
        showToast({
          state: 'ERROR',
          title: 'Error',
          message:
            'Issue when trying to rotate the application user secret. Please try again later',
          dismissible: true,
          dismissAfter: 3000,
        })
      );
    }
  };
};

export default function reducer(state = defaultState, action = {}) {
  switch (action.type) {
    case RESET_USERS_ROUTE_DATA:
      return { ...defaultState };
    case SET_USER_DATA:
      return {
        ...state,
        userData: action.payload,
      };
    case SET_USER_BRANDS:
      return {
        ...state,
        userBrands: action.payload,
      };
    case USER_PAGE_LOADING:
      return {
        ...state,
        pageLoading: action.payload,
      };
    case USER_DATA_LOADING:
      return {
        ...state,
        dataLoading: action.payload,
      };
    case SET_USER_SIMPLE_SSO_INTEGRATIONS:
      return {
        ...state,
        simpleSsoIntegrations: action.payload,
      };
    case SET_USER_SNOWFLAKE_CONFIG:
      return {
        ...state,
        snowflakeConfig: action.payload,
      };
    case SET_USER_HEX_CONFIG:
      return {
        ...state,
        hexConfig: action.payload,
      };
    case SET_USER_THOUGHTSPOT_CONFIG:
      return {
        ...state,
        thoughtSpotConfig: action.payload,
      };
    default:
      return state;
  }
}
